export type DisplayStatus = "All" | "Live" | "Completed" | "Review" | "Failed" | "Abandoned";

const LIVE = new Set(["joining", "listening", "speaking", "thinking", "queued", "running", "active", "live", "created"]);
const COMPLETED = new Set(["ended", "recorded", "completed"]);
const REVIEW = new Set(["review", "flagged"]);
const FAILED = new Set(["failed", "upload_failed", "error"]);
const ABANDONED = new Set(["abandoned"]);

export function toDisplayStatus(raw?: string | null): Exclude<DisplayStatus, "All"> {
  const s = String(raw || "").toLowerCase();
  if (REVIEW.has(s)) return "Review";
  if (FAILED.has(s)) return "Failed";
  if (ABANDONED.has(s)) return "Abandoned";
  if (COMPLETED.has(s)) return "Completed";
  if (LIVE.has(s)) return "Live";
  return "Completed";
}

export function matchesStatusFilter(raw: string | undefined, filter: DisplayStatus): boolean {
  if (filter === "All") return true;
  return toDisplayStatus(raw) === filter;
}

export function formatDuration(sec?: number | null, status?: string): string {
  const display = toDisplayStatus(status);
  if (display === "Live") {
    if (sec == null || !Number.isFinite(sec) || sec <= 0) return "Live";
    return `Live · ${formatClock(sec)}`;
  }
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return "—";
  return formatClock(sec);
}

function formatClock(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export function initialsFrom(text: string): string {
  const parts = text.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  const alnum = text.replace(/[^a-zA-Z0-9]/g, "");
  return (alnum.slice(0, 2) || "XX").toUpperCase();
}

const SESSION_USER_AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#236bd1,#7f5fdd)",
  "linear-gradient(135deg,#0aa888,#29a9cd)",
  "linear-gradient(135deg,#e27e55,#c55587)",
];

function hashStringStable(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Stable key for session list avatars (platform user id, then email, then display name). */
export function sessionUserAvatarKey(s: {
  ownerUserId?: string | null;
  userEmail?: string | null;
  userName?: string | null;
  sessionId: string;
}): string {
  const owner = String(s.ownerUserId || "").trim();
  if (owner) return owner;
  const email = String(s.userEmail || "").trim().toLowerCase();
  if (email) return email;
  const name = String(s.userName || "").trim().toLowerCase();
  if (name) return name;
  return s.sessionId;
}

export function avatarGradientForUserKey(key: string): string {
  const normalized = String(key || "").trim().toLowerCase();
  if (!normalized) return SESSION_USER_AVATAR_GRADIENTS[0];
  const idx = hashStringStable(normalized) % SESSION_USER_AVATAR_GRADIENTS.length;
  return SESSION_USER_AVATAR_GRADIENTS[idx];
}

export function displayUserName(s: {
  userName?: string | null;
  lastUserText?: string | null;
  sessionId: string;
}): string {
  if (s.userName) return s.userName;
  const hint = (s.lastUserText || "").trim();
  if (hint && hint.length < 40 && !hint.includes(" ")) return hint;
  return s.sessionId;
}

export function moodLabel(raw?: string | null): string {
  if (!raw) return "Neutral";
  const s = raw.toLowerCase();
  if (["joy", "positive", "amusement", "relief", "happy"].includes(s)) return "Positive";
  if (["engaged", "surprise", "curiosity", "engagement"].includes(s)) return "Engaged";
  if (["frustration", "anger", "frustrated", "sarcastic"].includes(s)) return "Frustrated";
  if (["fear", "sadness", "concern", "distress"].includes(s)) return "Concern";
  if (s === "neutral") return "Neutral";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Waveform key-moment dot colors (session-postprocess moodColor). */
export function moodColorForLabel(label: string): string {
  const s = String(label || "").toLowerCase();
  if (["joy", "amusement", "happy"].includes(s)) return "#0aa878";
  if (["sadness", "fear"].includes(s)) return "#64748b";
  if (["anger", "frustration"].includes(s)) return "#ef4444";
  if (["surprise"].includes(s)) return "#f59e0b";
  if (["neutral"].includes(s)) return "#8057e8";
  if (s === "guard") return "#ef4d55";
  return "#0968f2";
}

export function moodMomentChipStyle(label: string, color?: string | null): {
  color: string;
  background: string;
  borderColor: string;
} {
  const accent = (color && String(color).trim()) || moodColorForLabel(label);
  return {
    color: accent,
    background: `color-mix(in srgb, ${accent} 14%, white)`,
    borderColor: `color-mix(in srgb, ${accent} 38%, white)`,
  };
}

export function moodTone(mood: string): "green" | "blue" | "orange" | "red" | "gray" {
  if (mood === "Positive") return "green";
  if (mood === "Engaged") return "blue";
  if (mood === "Neutral") return "gray";
  if (mood === "Concern") return "orange";
  return "red";
}

export function statusTone(status: string): "green" | "blue" | "orange" | "red" | "gray" {
  if (status === "Completed") return "green";
  if (status === "Live") return "blue";
  if (status === "Review") return "orange";
  if (status === "Abandoned") return "gray";
  return "red";
}

export function formatStarted(iso?: string): { time: string; day: string } {
  if (!iso) return { time: "—", day: "" };
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return {
    time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    day: sameDay ? "Today" : d.toLocaleDateString(),
  };
}

/** Prefer stored topic only — never invent one from lastUserText. */
export function displayTopic(s: { topic?: string | null }): string {
  const topic = (s.topic || "").trim();
  return topic || "—";
}

/** Human label for persisted session endReason (Admin session detail). */
export function formatEndReason(endReason?: string | null): string | null {
  const r = String(endReason || "").trim().toLowerCase();
  if (!r) return null;
  switch (r) {
    case "session_limit":
      return "Session time limit";
    case "hangup":
      return "User ended call";
    case "idle":
      return "Idle timeout";
    case "timeout":
      return "Timed out";
    case "disconnect":
      return "Disconnected";
    case "pipeline_stalled":
      return "Pipeline stalled";
    default:
      return r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
